import { ActionContext } from "vuex";
import {
  PayloadEditApp,
  PayloadFacets,
  PayloadRemoveApp,
  PayloadReportApp,
  PayloadSaveApp,
  ShowMain,
  StateApp,
  StateEditApp,
  StateRemoveApp,
  StateReportApp,
  StateSaveApp,
  StateSearchApp,
} from "@/store/app/types_app";
import { App } from "@/model/App";
import { RootState } from "@/store";
import urls from "@/constants/urls";
import destore_service, { dataFacets } from "@/services/destore_service";
import router from "../../router";
import { ROUTES } from "@/constants/routes";

export const app_store = {
  state: {
    app: null,
    appStatusSave: StateSaveApp.initial,
    appStatusEdit: StateEditApp.initial,
    appStatusRemove: StateRemoveApp.initial,
    appStatusReport: StateReportApp.initial,
    appStatusSearch: StateSearchApp.loading,
    apps: [],
    facetsSelected: [],
    facets: [],
    showMainComponent: {
      showHome: true,
      showRes: false,
    },
  },
  mutations: {
    setApp(state: StateApp, app: App): void {
      state.app = app;
    },
    updateApp(state: StateApp, appUpdated: App): void {
      const index = state.apps.findIndex(
        (app) => app.appName === appUpdated.appName
      );
      if (index !== -1) state.apps.splice(index, 1, appUpdated);
    },
    setAppStatusSave(state: StateApp, appStatusSave: StateSaveApp): void {
      state.appStatusSave = appStatusSave;
    },
    setAppStatusEdit(state: StateApp, appStatusEdit: StateEditApp): void {
      state.appStatusEdit = appStatusEdit;
    },
    setAppStatusSearch(state: StateApp, appStatusSearch: StateSearchApp): void {
      state.appStatusSearch = appStatusSearch;
    },
    setApps(state: StateApp, apps: App[]): void {
      state.apps = apps;
    },
    setFacetsSelected(state: StateApp, facetsSelected: string[]): void {
      state.facetsSelected = facetsSelected;
    },
    setFacets(state: StateApp, facets: string[]): void {
      state.facets = facets;
    },
    setAppStatusRemove(state: StateApp, appStatusRemove: StateRemoveApp): void {
      state.appStatusRemove = appStatusRemove;
    },
    setAppStatusReport(state: StateApp, appStatusReport: StateReportApp): void {
      state.appStatusReport = appStatusReport;
    },
    setShowMainComponent(state: StateApp, showState: ShowMain): void {
      state.showMainComponent = showState;
    },
  },
  actions: {
    saveApp(
      context: ActionContext<StateApp, RootState>,
      payload: PayloadSaveApp
    ): void {
      context.commit("setAppStatusSave", StateSaveApp.loading);
      const URL_TO_SAVE = urls.url_to_save + payload.sessionId;
      const formData = new FormData();
      const app: App = payload.app;
      formData.append("appName", app.appName);
      formData.append("appDescription", app.appDescription);
      formData.append("appSummary", app.appSummary);
      formData.append("appUrl", app.appUrl);
      formData.append("appIcon", app.appIcon!);
      formData.append("appBanner", app.appBanner!);
      app.appImages.forEach((val: File | string, index: number) =>
        formData.append("appImages_" + index, val)
      );
      app.appVideos.forEach((val, index) =>
        formData.append("appVideos_" + index, val)
      );
      app.appCategories.forEach((val, index) =>
        formData.append("appCategories_" + index, val)
      );

      const options = {
        method: "POST", // or 'PUT'
        body: formData,
      };
      fetch(URL_TO_SAVE, options)
        .then((response) => {
          if (response.status === 401) {
            router.push(ROUTES.SESSION_EXPIRED.path);
            return;
          }
          if (!response.ok) {
            throw Error();
          }
          return response.json();
        })
        .then(() => {
          context.commit("setApp", app);
          context.commit("setAppStatusSave", StateSaveApp.success);
        })
        .catch(() => {
          context.commit("setAppStatusSave", StateSaveApp.fail);
        });
    },
    editApp(
      context: ActionContext<StateApp, RootState>,
      payload: PayloadEditApp
    ): void {
      context.commit("setAppStatusEdit", StateEditApp.loading);
      const URL_TO_EDIT =
        urls.url_to_update_app +
        payload.appUpdated.appName +
        "/" +
        payload.sessionId;
      const formData = new FormData();
      const app: App = payload.appUpdated;
      formData.append("appDescription", app.appDescription);
      formData.append("appSummary", app.appSummary);
      formData.append("appUrl", app.appUrl);
      app.appCategories.forEach((val, index) =>
        formData.append("appCategories_" + index, val)
      );

      const options = {
        method: "POST", // or 'PUT'
        body: formData,
      };
      fetch(URL_TO_EDIT, options)
        .then((response) => {
          if (response.status === 401) {
            router.push(ROUTES.SESSION_EXPIRED.path);
            return;
          }
          if (!response.ok) {
            throw Error();
          }
          return response.json();
        })
        .then(() => {
          context.commit("updateApp", app);
          context.commit("setAppStatusEdit", StateEditApp.success);
        })
        .catch(() => {
          context.commit("setAppStatusEdit", StateEditApp.fail);
        });
    },
    removeApp(
      context: ActionContext<StateApp, RootState>,
      payload: PayloadRemoveApp
    ): void {
      context.commit("setAppStatusRemove", StateRemoveApp.loading);
      const URL_TO_REMOVE =
        urls.url_to_delete_app + payload.appName + "/" + payload.sessionId;
      console.log(URL_TO_REMOVE);
      const options = {
        method: "GET", // or 'PUT'
      };
      fetch(URL_TO_REMOVE, options)
        .then((response) => {
          if (response.status === 401) {
            router.push(ROUTES.SESSION_EXPIRED.path);
            return;
          }
          if (!response.ok) {
            throw Error();
          }
          return response.json();
        })
        .then(() => {
          context.commit("setAppStatusRemove", StateRemoveApp.success);
        })
        .catch(() => {
          context.commit("setAppStatusRemove", StateRemoveApp.fail);
        });
    },
    reportApp(
      context: ActionContext<StateApp, RootState>,
      payload: PayloadReportApp
    ): void {
      context.commit("setAppStatusReport", StateReportApp.loading);
      const URL_TO_REPORT = urls.url_to_report_app;
      const formData = new FormData();
      formData.append("appName", payload.appName);
      formData.append("cause", payload.cause);
      formData.append("comments", payload.comments);
      formData.append("informerEmail", payload.informerEmail);
      const options = {
        method: "POST", // or 'PUT'
        body: formData,
      };
      fetch(URL_TO_REPORT, options)
        .then((response) => {
          if (!response.ok) {
            throw Error();
          }
          return response.json();
        })
        .then(() => {
          context.commit("setAppStatusReport", StateReportApp.success);
        })
        .catch(() => {
          context.commit("setAppStatusReport", StateReportApp.fail);
        });
    },
    searchForAnApp(
      context: ActionContext<StateApp, RootState>,
      appName: string
    ): void {
      const URL_TO_SEARCH = urls.url_to_search + appName;
      const options = {
        method: "GET", // or 'PUT'
      };
      context.commit("setAppStatusSearch", StateSearchApp.loading);
      let facets: string[] = [];
      fetch(URL_TO_SEARCH, options)
        .then((response) => response.json())
        .then((data) => {
          facets = data.message.aggregation.map((aggregation: any) => {
            return aggregation.key + " (" + aggregation.doc_count + ")";
          });
          context.commit("setFacets", facets);
          if (data.message.hits.length > 0) {
            const dataParse = data.message.hits.map((app: any) => {
              return app._source;
            });
            context.commit("setApps", dataParse);
            context.commit("setAppStatusSearch", StateSearchApp.success);
          } else {
            context.commit("setApps", []);
            context.commit("setAppStatusSearch", StateSearchApp.fail);
          }
        })
        .catch((error) => {
          console.error("Error:", error);
          context.commit("setAppStatusSearch", StateSearchApp.fail);
        });
    },
    searchIfFacetIsSelected(
      context: ActionContext<StateApp, RootState>,
      facet: string
    ): boolean {
      return context.state.facetsSelected.includes(facet);
    },
    selectFacets(
      context: ActionContext<StateApp, RootState>,
      facet: string
    ): void {
      context.state.facetsSelected.push(facet);
      //context.commit("setFacetsSelected", facet);
    },
    changeFacets(
      context: ActionContext<StateApp, RootState>,
      facet: string[]
    ): void {
      context.commit("setFacetsSelected", facet);
      //context.commit("setFacetsSelected", facet);
    },
    searchAppsByCategory(
      context: ActionContext<StateApp, RootState>,
      payloadFacets: PayloadFacets
    ): void {
      context.commit("setAppStatusSearch", StateSearchApp.loading);
      let dataParsed: dataFacets = {
        apps: [],
        categories: [],
      };
      let URL_TO_SEARCH = "";
      if (payloadFacets.facetsSelected.length > 0) {
        URL_TO_SEARCH =
          urls.url_to_search +
          payloadFacets.appName +
          "&appCategories=" +
          destore_service.parseFacet(payloadFacets.facetsSelected);
      } else {
        URL_TO_SEARCH = urls.url_to_search + payloadFacets.appName;
      }
      const options = {
        method: "GET", // or 'PUT'
      };
      fetch(URL_TO_SEARCH, options)
        .then((response) => response.json())
        .then((data) => {
          dataParsed = destore_service.parseData(data);
          if (dataParsed.apps.length !== 0) {
            context.commit("setApps", dataParsed.apps);
            context.commit("setFacets", dataParsed.categories);
            context.commit("setAppStatusSearch", StateSearchApp.success);
          } else {
            context.commit("setApps", []);
            context.commit("setFacets", []);
            context.commit("setAppStatusSearch", StateSearchApp.fail);
          }
        })
        .catch((error) => {
          console.error("Error:", error);
          context.commit("setAppStatusSearch", StateSearchApp.fail);
        });
    },
    removeApps(context: ActionContext<StateApp, RootState>): void {
      context.commit("setApps", []);
    },
    changeShowMainComponent(
      context: ActionContext<StateApp, RootState>,
      showMain: ShowMain
    ): void {
      context.commit("setShowMainComponent", showMain);
    },
    removeTagList(context: ActionContext<StateApp, RootState>): void {
      context.commit("setFacetsSelected", []);
    },
    removeFacetSelected(
      context: ActionContext<StateApp, RootState>,
      categorySelected: string
    ): void {
      const index = context.state.facetsSelected.findIndex(
        (category) => category === categorySelected
      );
      context.state.facetsSelected.splice(index, 1);
    },
  },
  getters: {
    getStateSaveApp(state: StateApp): StateSaveApp {
      return state.appStatusSave;
    },
    getStateRemoveApp(state: StateApp): StateRemoveApp {
      return state.appStatusRemove;
    },
    getStateSearchApp(state: StateApp): StateSearchApp {
      return state.appStatusSearch;
    },
    getStateReportApp(state: StateApp): StateReportApp {
      return state.appStatusReport;
    },
    getStateEditApp(state: StateApp): StateEditApp {
      return state.appStatusEdit;
    },
    getApps(state: StateApp): App[] {
      return state.apps;
    },
    getFacets(state: StateApp): string[] {
      return state.facets;
    },
    getFacetsSelected(state: StateApp): string[] {
      return state.facetsSelected;
    },
    getShowMainComponent(state: StateApp): ShowMain {
      return state.showMainComponent;
    },
  },
};
